import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
    useActivateCapitalAccountMutation,
    useCreateCapitalAccountMutation,
    useGetCapitalAccountsQuery,
    useGetShareSubcategoriesQuery,
    useTestCapitalAccountMutation,
    useUpdateCapitalAccountMutation,
    useSimulateCapitalAccountMutation,
} from 'api/main';
import { EURO_SYMBOL, billStatus as capitalAccountStatus } from 'utils/constants';
import { getShareSubcategoryIdFromIRI } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import CapitalAccountForm from './CapitalAccountForm';
import { MenuActivate, MenuTest, MenuSimulate } from 'components/ActionsMenu/actions';
import { ActionsMenu, ConfirmDialog, CustomSelect, Loader } from 'components';
import {
    Box,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';

const tableHead = (
    <TableHead>
        <TableRow>
            <TableCell width="8%">{texts.CATEGORIE}</TableCell>
            <TableCell>VL ({EURO_SYMBOL})</TableCell>
            <TableCell>{texts.DATE}</TableCell>
        </TableRow>
    </TableHead>
);

const ActivateActionItem = React.forwardRef((props, ref) => <MenuActivate ref={ref} {...props} />);
const TestActionItem = React.forwardRef((props, ref) => <MenuTest ref={ref} {...props} />);
const SimulateActionItem = React.forwardRef((props, ref) => <MenuSimulate ref={ref} {...props} />);

const CapitalAccountManagement = ({ fundId }) => {
    const { data: capitalAccounts = [], isFetching: dataLoading } = useGetCapitalAccountsQuery({
        'shareSubcategory.shareCategory.investmentFund': fundId,
        pagination: false,
    });
    const [createCapitalAccount, { isLoading: createLoading }] = useCreateCapitalAccountMutation();
    const [updateCapitalAccount, { isLoading: updateLoading }] = useUpdateCapitalAccountMutation();
    const [testCapitalAccount, { isLoading: testLoading }] = useTestCapitalAccountMutation();
    const [simulateCapitalAccount, { isLoading: simulateLoading }] =
        useSimulateCapitalAccountMutation();
    const [activateCapitalAccount, { isLoading: activateLoading, isSuccess: activateSuccess }] =
        useActivateCapitalAccountMutation();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery({
            displayUnavailable: 1,
            'shareCategory.investmentFund': fundId,
        });

    const [selectedDate, setSelectedDate] = useState('');
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);

    const createdCapitalAccounts = useMemo(
        () => capitalAccounts.filter(ca => ca.status === capitalAccountStatus.CREATED),
        [capitalAccounts]
    );

    const activatedCapitalAcocunts = useMemo(
        () => capitalAccounts.filter(ca => ca.status === capitalAccountStatus.ACTIVATED),
        [capitalAccounts]
    );

    const completedCapitalAcocunts = useMemo(
        () => capitalAccounts.filter(ca => ca.status === capitalAccountStatus.COMPLETED),
        [capitalAccounts]
    );

    const activationDates = useMemo(() => {
        const dates = [...activatedCapitalAcocunts, ...completedCapitalAcocunts].map(ca => ca.date);
        return [...new Set(dates)];
    }, [activatedCapitalAcocunts, completedCapitalAcocunts]);

    const handleActivate = useCallback(() => {
        activateCapitalAccount(fundId);
    }, [fundId, activateCapitalAccount]);

    const handleTest = useCallback(() => {
        testCapitalAccount(fundId);
    }, [fundId, testCapitalAccount]);

    const handleSimulate = useCallback(() => {
        simulateCapitalAccount(fundId);
    }, [fundId, simulateCapitalAccount]);

    useEffect(() => {
        if (activateSuccess) {
            setActivateDialogOpen(false);
        }
    }, [activateSuccess]);

    useEffect(() => {
        if (activationDates.length) {
            setSelectedDate(activationDates[0]);
        }
    }, [activationDates]);

    return (
        <React.Fragment>
            <ConfirmDialog
                open={activateDialogOpen}
                title={texts.CAPITAL_ACCOUNT_ACTIVATION}
                cancelText={texts.CANCEL}
                confirmText={texts.CONFIRMER}
                loading={activateLoading}
                handleClose={() => setActivateDialogOpen(false)}
                handleConfirm={handleActivate}
            >
                {texts.CONFIRM_CAPITAL_ACCOUNT_ACTIVATION}
            </ConfirmDialog>
            <Box marginY={3} textAlign="center">
                <Loader loading={subcategoriesLoader || dataLoading}>
                    <CapitalAccountForm
                        activationDates={activationDates}
                        createdList={createdCapitalAccounts}
                        completedList={completedCapitalAcocunts}
                        tableHead={tableHead}
                        shareSubcategories={shareSubcategories}
                        dataLoading={dataLoading}
                        onAdd={createCapitalAccount}
                        onUpdate={updateCapitalAccount}
                        loading={createLoading || updateLoading}
                    />
                    <Box paddingTop={4}>
                        {!createLoading && !!createdCapitalAccounts.length && (
                            <ActionsMenu
                                autoClose
                                justify="center"
                                buttonStyle="text"
                                buttonText={texts.SEND_CAPITAL_ACCOUNT}
                            >
                                <ActivateActionItem
                                    disabled={testLoading}
                                    onClick={() => setActivateDialogOpen(true)}
                                />
                                <TestActionItem disabled={testLoading} onClick={handleTest} />
                                <SimulateActionItem
                                    disabled={simulateLoading}
                                    onClick={handleSimulate}
                                />
                            </ActionsMenu>
                        )}
                    </Box>
                    {!!activationDates.length && (
                        <React.Fragment>
                            <Box paddingTop={4} justifyContent="center" display="flex">
                                <CustomSelect
                                    value={selectedDate}
                                    onChange={e => setSelectedDate(e.target.value)}
                                    label={texts.VL_HISTORY}
                                >
                                    {activationDates.map(date => (
                                        <MenuItem key={date} value={date}>
                                            {moment(date).format('L')}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            </Box>
                            <TableContainer>
                                <Table>
                                    {tableHead}
                                    <TableBody>
                                        {[...activatedCapitalAcocunts, ...completedCapitalAcocunts]
                                            .filter(ca =>
                                                moment(ca.date).isSame(moment(selectedDate), 'day')
                                            )
                                            .map((ca, i) => (
                                                <TableRow key={ca.id}>
                                                    <TableCell>
                                                        {
                                                            shareSubcategories.find(
                                                                sc =>
                                                                    sc.id ===
                                                                    getShareSubcategoryIdFromIRI(
                                                                        ca.shareSubcategory
                                                                    )
                                                            )?.name
                                                        }
                                                    </TableCell>
                                                    <TableCell>{ca.vl}</TableCell>
                                                    {i === 0 && (
                                                        <TableCell rowSpan={7}>
                                                            {moment(ca.date).format('L')}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </React.Fragment>
                    )}
                </Loader>
            </Box>
        </React.Fragment>
    );
};

CapitalAccountManagement.propTypes = {
    fundId: PropTypes.number,
};

export default CapitalAccountManagement;
